import { useState } from 'react'
import { motion } from 'framer-motion'
import { Loader2, AlertCircle, RefreshCw, Clock, AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useCaseStatus, type CompactMilestoneStatus } from './useCaseStatus'
import { NextStepButton } from './NextStepButton'
import {
  type NextStep,
  phaseConfig,
  accentColorClasses,
  springConfig,
} from './constants'

interface CaseStatusCardProps {
  caseId: number
  onGenerateDocument: (templateType: string) => Promise<void>
  onOpenActionModal: (preselectedActionType?: string) => void
  generatingDocType?: string | null
}

export function CaseStatusCard({
  caseId,
  onGenerateDocument,
  onOpenActionModal,
  generatingDocType,
}: CaseStatusCardProps) {
  const {
    phase,
    phaseConfig: currentPhaseConfig,
    phaseSince,
    compactMilestones,
    nextSteps,
    deadline,
    isLoading,
    error,
    refetch,
  } = useCaseStatus(caseId)

  const PhaseIcon = currentPhaseConfig.icon
  const accentColors = accentColorClasses[currentPhaseConfig.accentColor]

  // Format the "since" date
  const formattedDate = phaseSince
    ? new Date(phaseSince).toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
      })
    : null

  // Handle next step button click
  const handleStepClick = async (step: NextStep) => {
    if (step.actionType === 'generate_document' && step.documentType) {
      await onGenerateDocument(step.documentType)
    } else if (step.actionType === 'open_modal') {
      onOpenActionModal(step.preselectedActionType)
    }
  }

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8">
        <div className="flex flex-col items-center justify-center">
          <Loader2 className="h-8 w-8 text-slate-400 animate-spin mb-3" />
          <p className="text-slate-500 text-sm">Status wird geladen...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8">
        <div className="flex flex-col items-center justify-center">
          <AlertCircle className="h-10 w-10 text-red-300 mb-3" />
          <p className="text-slate-700 font-medium mb-1">Fehler beim Laden</p>
          <p className="text-slate-500 text-sm mb-3">{error}</p>
          <Button variant="outline" size="sm" onClick={refetch} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Erneut versuchen
          </Button>
        </div>
      </div>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={springConfig.default}
      className={`relative overflow-hidden rounded-xl bg-gradient-to-br ${currentPhaseConfig.gradient} border border-slate-200 shadow-sm`}
    >
      {/* Dot pattern overlay */}
      <div
        className="absolute inset-0 opacity-[0.02] pointer-events-none"
        style={{
          backgroundImage: `radial-gradient(circle, currentColor 1px, transparent 1px)`,
          backgroundSize: '20px 20px',
        }}
      />

      <div className="relative p-5">
        {/* Header: Phase indicator */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={springConfig.bouncy}
              className={`flex items-center justify-center w-10 h-10 rounded-xl ${accentColors.bg} shadow-md`}
            >
              <PhaseIcon className="w-5 h-5 text-white" />
            </motion.div>
            <div>
              <h3 className="font-semibold text-slate-900">{currentPhaseConfig.label}</h3>
              {formattedDate && (
                <p className="text-xs text-slate-500">seit {formattedDate}</p>
              )}
            </div>
          </div>
        </div>

        {/* Compact milestone progress */}
        <div className="mb-5">
          <div className="flex items-center gap-1">
            {compactMilestones.map((milestone, index) => (
              <div key={milestone.id} className="flex items-center flex-1">
                <MilestoneDot milestone={milestone} />
                {index < compactMilestones.length - 1 && (
                  <div
                    className={`h-0.5 flex-1 mx-1 ${
                      milestone.status === 'completed'
                        ? 'bg-emerald-300'
                        : 'bg-slate-200'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="flex items-center mt-1.5">
            {compactMilestones.map((milestone) => (
              <div key={`label-${milestone.id}`} className="flex-1 text-center">
                <span
                  className={`text-[10px] ${
                    milestone.status === 'current'
                      ? 'text-slate-700 font-medium'
                      : milestone.status === 'completed'
                        ? 'text-slate-600'
                        : 'text-slate-400'
                  }`}
                >
                  {milestone.shortLabel}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-slate-200/50 mb-5" />

        {/* Next Steps */}
        <div>
          <h4 className="text-sm font-medium text-slate-700 mb-3">Nächste Schritte</h4>
          <div className="grid grid-cols-3 gap-3">
            {nextSteps.map((step) => (
              <NextStepButton
                key={step.id}
                step={step}
                isLoading={generatingDocType === step.documentType}
                onClick={() => handleStepClick(step)}
              />
            ))}
          </div>
        </div>

        {/* Deadline warning (if applicable) */}
        {deadline && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springConfig.default, delay: 0.2 }}
            className={`mt-4 flex items-center gap-2 p-3 rounded-lg ${
              deadline.urgency === 'urgent'
                ? 'bg-red-50 border border-red-200'
                : deadline.urgency === 'warning'
                  ? 'bg-amber-50 border border-amber-200'
                  : 'bg-blue-50 border border-blue-200'
            }`}
          >
            {deadline.urgency === 'urgent' ? (
              <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
            ) : (
              <Clock className="w-4 h-4 text-amber-500 flex-shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <span
                className={`text-sm font-medium ${
                  deadline.urgency === 'urgent'
                    ? 'text-red-700'
                    : deadline.urgency === 'warning'
                      ? 'text-amber-700'
                      : 'text-blue-700'
                }`}
              >
                {deadline.label}:{' '}
                {new Date(deadline.deadline).toLocaleDateString('de-DE', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                })}
              </span>
              <span
                className={`text-sm ml-2 ${
                  deadline.urgency === 'urgent'
                    ? 'text-red-600'
                    : deadline.urgency === 'warning'
                      ? 'text-amber-600'
                      : 'text-blue-600'
                }`}
              >
                ({deadline.daysUntil === 0
                  ? 'Heute'
                  : deadline.daysUntil === 1
                    ? 'Morgen'
                    : `in ${deadline.daysUntil} Tagen`})
              </span>
            </div>
          </motion.div>
        )}
      </div>
    </motion.div>
  )
}

// Compact milestone dot component
function MilestoneDot({ milestone }: { milestone: CompactMilestoneStatus }) {
  const getStatusStyles = () => {
    switch (milestone.status) {
      case 'completed':
        return 'bg-emerald-500 border-emerald-300'
      case 'current':
        return 'bg-blue-500 border-blue-300 ring-4 ring-blue-500/20'
      case 'skipped':
        return 'bg-slate-300 border-slate-200'
      default:
        return 'bg-slate-200 border-slate-100'
    }
  }

  return (
    <motion.div
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={springConfig.bouncy}
      className={`w-3 h-3 rounded-full border-2 ${getStatusStyles()}`}
    />
  )
}
